import { useState, useEffect, useRef } from 'react'
import { supabase } from '../services/supabase'

async function loadPermissions(userEmail) {
  try {
    if (!userEmail) return null

    const { data: advisor } = await supabase
      .from('advisors')
      .select('id, is_owner')
      .eq('email', userEmail)
      .maybeSingle()

    if (!advisor) return null

    // Owners get full access — bypass role-based permission checks
    if (advisor.is_owner) return null

    const { data: roleAssignments } = await supabase
      .from('advisor_roles')
      .select('role_id')
      .eq('advisor_id', advisor.id)

    if (!roleAssignments || roleAssignments.length === 0) return null

    const roleIds = roleAssignments.map(r => r.role_id)

    const { data: perms } = await supabase
      .from('role_permissions')
      .select('page_key')
      .in('role_id', roleIds)
      .eq('can_view', true)

    return new Set(perms?.map(p => p.page_key) || [])
  } catch {
    return null // fail open — full access
  }
}

export function useAuth() {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(null)
  const [loading, setLoading] = useState(true)
  const userRef = useRef(null)
  const startTimeRef = useRef(null)

  useEffect(() => {
    const minimumLoadingTime = 1000
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now()
    }

    const finishLoading = (resetTimer = false) => {
      if (resetTimer) {
        startTimeRef.current = Date.now()
      }

      const elapsed = Date.now() - startTimeRef.current
      if (elapsed < minimumLoadingTime) {
        setTimeout(() => setLoading(false), minimumLoadingTime - elapsed)
      } else {
        setLoading(false)
      }
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user || null
      setUser(u)
      userRef.current = u
      if (u) loadPermissions(u.email).then(setPermissions)
      finishLoading()
    })

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user || null

      // If user just logged in, reset the timer for the loading screen
      if (u && !userRef.current) {
        setLoading(true)
        finishLoading(true) // Reset timer when user logs in
      } else {
        finishLoading()
      }

      setUser(u)
      userRef.current = u
      if (u) loadPermissions(u.email).then(setPermissions)
      else setPermissions(null)
    })

    // Realtime: instantly apply permission changes to active sessions (optional, gracefully fails if unavailable)
    let channel
    try {
      channel = supabase
        .channel('permissions-live')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'role_permissions' }, () => {
          if (userRef.current) loadPermissions(userRef.current.email).then(setPermissions)
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'advisor_roles' }, () => {
          if (userRef.current) loadPermissions(userRef.current.email).then(setPermissions)
        })

      channel.subscribe((status) => {
        if (status === 'SUBSCRIPTION_ERROR') {
          console.debug('Real-time subscription unavailable (non-critical)')
        }
      })
    } catch (err) {
      console.debug('Real-time features disabled:', err.message)
    }

    return () => {
      subscription?.unsubscribe()
      if (channel) supabase.removeChannel(channel)
    }
  }, [])

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setPermissions(null)
    userRef.current = null
  }

  const canView = (pageKey) => {
    if (permissions === null) return true
    return permissions.has(pageKey)
  }

  /**
   * Check granular permissions for data visibility and features
   * Examples: canAccess('can_view_advisor_commissions'), canAccess('can_view_all_clients')
   */
  const canAccess = (permissionKey) => {
    if (permissions === null) return true
    return permissions.has(permissionKey)
  }

  return { user, loading, signOut, permissions, canView, canAccess }
}
